<script lang="ts">
  import BottomSheet from './BottomSheet.svelte';
  import type { ItemSet, SetId } from '../lib/types';

  interface Props {
    open: boolean;
    sets: ItemSet[];
    currentSetId: SetId;
    onselect: (id: SetId) => void;
    onadd: (name: string) => void;
    onrename: (id: SetId, name: string) => void;
    ondelete: (id: SetId) => void;
    onclose: () => void;
  }

  let {
    open,
    sets,
    currentSetId,
    onselect,
    onadd,
    onrename,
    ondelete,
    onclose,
  }: Props = $props();

  let editingId = $state<SetId | null>(null);
  let editingName = $state('');
  let showAdd = $state(false);
  let newName = $state('');
  let confirmDeleteId = $state<SetId | null>(null);

  // open が変わったら一時状態をリセット
  $effect(() => {
    if (!open) {
      editingId = null;
      editingName = '';
      showAdd = false;
      newName = '';
      confirmDeleteId = null;
    }
  });

  function startRename(set: ItemSet): void {
    editingId = set.id;
    editingName = set.name;
  }

  function commitRename(): void {
    if (editingId && editingName.trim()) {
      onrename(editingId, editingName.trim());
    }
    editingId = null;
    editingName = '';
  }

  function cancelRename(): void {
    editingId = null;
    editingName = '';
  }

  function commitAdd(): void {
    const name = newName.trim();
    if (name) {
      onadd(name);
      onclose();
    }
    newName = '';
    showAdd = false;
  }

  function handleSelect(id: SetId): void {
    onselect(id);
    onclose();
  }

  function requestDelete(set: ItemSet): void {
    if (sets.length <= 1) return;
    confirmDeleteId = set.id;
  }

  function confirmDelete(): void {
    if (confirmDeleteId) {
      ondelete(confirmDeleteId);
      confirmDeleteId = null;
    }
  }
</script>

<BottomSheet {open} title="セット管理" {onclose}>
  <ul class="set-list">
    {#each sets as set (set.id)}
      <li class="set-row" class:active={set.id === currentSetId}>
        {#if editingId === set.id}
          <input
            class="rename-input"
            bind:value={editingName}
            onkeydown={(e) => {
              if (e.key === 'Enter') commitRename();
              else if (e.key === 'Escape') cancelRename();
            }}
            onblur={commitRename}
          />
        {:else if confirmDeleteId === set.id}
          <span class="confirm-text">削除しますか？</span>
          <button class="confirm-no" onclick={() => (confirmDeleteId = null)}>
            キャンセル
          </button>
          <button class="confirm-yes" onclick={confirmDelete}>削除</button>
        {:else}
          <button
            class="set-select"
            onclick={() => handleSelect(set.id)}
          >
            <span class="check">{set.id === currentSetId ? '●' : '○'}</span>
            <span class="set-name">{set.name}</span>
            <span class="item-count">{set.items.length}項目</span>
          </button>
          <button
            class="action-btn"
            onclick={() => startRename(set)}
            aria-label="改名"
          >
            ✎
          </button>
          <button
            class="action-btn delete"
            onclick={() => requestDelete(set)}
            disabled={sets.length <= 1}
            aria-label="削除"
          >
            🗑
          </button>
        {/if}
      </li>
    {/each}
  </ul>

  {#if showAdd}
    <div class="add-form">
      <input
        class="add-input"
        bind:value={newName}
        placeholder="新しいセット名"
        onkeydown={(e) => {
          if (e.key === 'Enter') commitAdd();
          else if (e.key === 'Escape') {
            showAdd = false;
            newName = '';
          }
        }}
      />
      <button class="primary" onclick={commitAdd} disabled={!newName.trim()}>
        追加
      </button>
      <button
        class="cancel"
        onclick={() => {
          showAdd = false;
          newName = '';
        }}
      >
        取消
      </button>
    </div>
  {:else}
    <button class="add-btn" onclick={() => (showAdd = true)}>＋ 新規セット</button>
  {/if}
</BottomSheet>

<style>
  .set-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .set-row {
    display: flex;
    align-items: center;
    gap: 4px;
    border-bottom: 1px solid var(--color-border);
    min-height: 52px;
    padding: 0 4px;
  }

  .set-row.active {
    background: rgba(233, 69, 96, 0.07);
  }

  .set-select {
    flex: 1;
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding: 12px var(--space-sm);
    text-align: left;
    min-width: 0;
  }

  .check {
    color: var(--color-accent);
    font-size: 14px;
    flex-shrink: 0;
    width: 16px;
    text-align: center;
  }

  .set-name {
    flex: 1;
    font-size: var(--font-size-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .item-count {
    font-size: 11px;
    color: var(--color-fg-muted);
    flex-shrink: 0;
    background: var(--color-border);
    padding: 2px 8px;
    border-radius: 100px;
  }

  .action-btn {
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-fg-muted);
    border-radius: var(--radius-sm);
    flex-shrink: 0;
    font-size: 14px;
  }

  .action-btn:active:not(:disabled) {
    background: var(--color-border);
  }

  .action-btn:disabled {
    opacity: 0.25;
  }

  .rename-input {
    flex: 1;
    padding: 10px;
    border: 2px solid var(--color-accent);
    border-radius: var(--radius-sm);
    background: var(--color-bg);
    color: var(--color-fg);
    font-size: var(--font-size-base);
    margin: 6px 0;
  }

  .confirm-text {
    flex: 1;
    font-size: var(--font-size-sm);
    color: var(--color-accent);
    font-weight: 600;
    padding-left: var(--space-sm);
  }

  .confirm-no,
  .confirm-yes {
    padding: 8px 12px;
    border-radius: var(--radius-sm);
    font-size: var(--font-size-sm);
    font-weight: 600;
  }

  .confirm-no {
    background: var(--color-border);
    color: var(--color-fg);
  }

  .confirm-yes {
    background: var(--color-accent);
    color: var(--color-accent-fg);
  }

  .add-btn {
    width: 100%;
    padding: 14px;
    margin-top: var(--space-md);
    border: 1px dashed var(--color-border);
    border-radius: var(--radius-sm);
    color: var(--color-fg-muted);
    font-weight: 500;
    font-size: var(--font-size-base);
  }

  .add-btn:active {
    background: var(--color-border);
  }

  .add-form {
    display: flex;
    gap: var(--space-sm);
    margin-top: var(--space-md);
    flex-wrap: wrap;
  }

  .add-input {
    flex: 1 1 100%;
    padding: 10px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    background: var(--color-bg);
    color: var(--color-fg);
    font-size: var(--font-size-base);
  }

  .add-input:focus {
    outline: 2px solid var(--color-accent);
    outline-offset: -1px;
  }

  .add-form button {
    flex: 1;
    padding: 10px var(--space-md);
    border-radius: var(--radius-sm);
    font-weight: 600;
    font-size: var(--font-size-sm);
  }

  .add-form button:disabled {
    opacity: 0.4;
  }

  .add-form .primary {
    background: var(--color-accent);
    color: var(--color-accent-fg);
  }

  .add-form .cancel {
    background: var(--color-border);
    color: var(--color-fg);
  }
</style>
